/**
 * ExecutionDetailsView — the execution's headline identifier fields plus the
 * decomposed executionDetails document.
 *
 * executionDetails is polymorphic by executionType; the mapper splits it into
 * presence-driven blocks and this component renders only the ones that are
 * present: scalar fields, the bank summary, the processor response, the
 * captured responses (raw bodies in a JSON view) and any free-form response
 * payload. Pure presentational.
 */
import Box from '@cloudscape-design/components/box';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { CopyableText } from 'src/components/common/CopyableText';
import { JsonView } from 'src/components/common/JsonView';
import { ExecutionDetailView } from 'src/store/slices/executionDetail/executionDetailTypes';
import { KeyValue } from 'src/store/slices/paymentDetail/paymentDetailTypes';

/** Uppercase the first letter of a camelCase label: executionType → ExecutionType. */
const upperFirst = (label: string): string => (label ? label.charAt(0).toUpperCase() + label.slice(1) : label);

const kvItems = (fields: KeyValue[]) =>
  fields.map(field => ({ label: upperFirst(field.key), value: <CopyableText value={field.value} /> }));

/** A labelled KeyValuePairs block, rendered only when it has fields. */
const FieldBlock = ({ label, fields, columns }: { label: string; fields: KeyValue[]; columns: number }) => {
  if (fields.length === 0) return null;
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <KeyValuePairs columns={columns} items={kvItems(fields)} />
    </div>
  );
};

interface ExecutionDetailsViewProps {
  detail: ExecutionDetailView;
}

export const ExecutionDetailsView = ({ detail }: ExecutionDetailsViewProps) => (
  <SpaceBetween size="m">
    <KeyValuePairs columns={4} items={kvItems(detail.identifiers)} />

    <FieldBlock label="Execution details" fields={detail.detailFields} columns={4} />
    <FieldBlock label="Bank summary" fields={detail.bankSummary} columns={3} />
    <FieldBlock label="Processor response" fields={detail.processorResponse} columns={3} />

    {detail.responses.length > 0 && (
      <div>
        <Box variant="awsui-key-label">Captured responses ({detail.responses.length})</Box>
        <SpaceBetween size="xs">
          {detail.responses.map(response => (
            <ExpandableSection key={response.id} headerText={response.heading}>
              <JsonView value={response.rawResponse} />
            </ExpandableSection>
          ))}
        </SpaceBetween>
      </div>
    )}

    {detail.oslResponse && (
      <div>
        <Box variant="awsui-key-label">Response payload</Box>
        <JsonView value={detail.oslResponse} />
      </div>
    )}
  </SpaceBetween>
);
